import { Component, inject, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import { primengImports } from '@app/core/primeng';
import { NotificationService } from '../../services/notification.service';
import { AuthService } from '../../services/auth.service';
import { AppNotification } from '../../models/notification.models';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, ...primengImports],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit {
  @ViewChild('notifPanel') notifPanel!: OverlayPanel;

  readonly svc   = inject(NotificationService);
  private auth   = inject(AuthService);
  private router = inject(Router);

  ngOnInit(): void {
    if (!this.svc.notifications().length && !this.svc.loading()) this.svc.fetch().subscribe();
    const userId = parseInt(this.auth.user?.id ?? '0', 10);
    if (userId) this.svc.connectReverb(userId);
  }

  toggle(event: MouseEvent): void {
    this.notifPanel.toggle(event);
  }

  open(n: AppNotification): void {
    this.svc.markAsRead(n.id);
    this.notifPanel.hide();
    if (n.link) this.router.navigateByUrl(n.link);
  }

  /** Icône spécifique au type d'événement (ex. facture payée, mention), repli sur l'icône du module. */
  eventIcon(n: AppNotification): string {
    const icons: Record<string, string> = {
      // Facturation (kreosi)
      'invoice.paid':           'fa-light fa-check-circle',
      'invoice.partially_paid': 'fa-light fa-money-bill',
      'invoice.overdue':        'fa-light fa-exclamation-circle',
      'quote.accepted':         'fa-light fa-thumbs-up',
      'quote.refused':          'fa-light fa-thumbs-down',
      // Tâches & projets (skedi)
      'task.comment':           'fa-light fa-comment',
      'task.mention':           'fa-light fa-at',
      'task.status':            'fa-light fa-sync',
      'task.assignee':          'fa-light fa-user-plus',
      'task.priority':          'fa-light fa-flag',
      'task.due_date':          'fa-light fa-calendar',
      'task.subtask':           'fa-light fa-sitemap',
      'task.attachment':        'fa-light fa-paperclip',
      'task.task_updated':      'fa-light fa-pencil',
      // Logistique (logistiki)
      'order.new':              'fa-light fa-shopping-cart',
      'order.shipped':          'fa-light fa-truck',
      'stock.low':              'fa-light fa-exclamation-triangle',
      // Site web (eezili) : demande reçue par un formulaire du site
      'form.submission':        'fa-light fa-inbox',
      // Système
      'user.invited':           'fa-light fa-envelope',
      'user.joined':            'fa-light fa-user',
    };
    return icons[n.event_type] ?? this.moduleIcon(n.module);
  }

  /** Couleur sémantique pour certains événements (succès/alerte), repli sur la couleur du module. */
  eventColor(n: AppNotification): string {
    const colors: Record<string, string> = {
      'invoice.paid':           '#10B981',
      'invoice.partially_paid': '#F59E0B',
      'invoice.overdue':        '#EF4444',
      'quote.accepted':         '#10B981',
      'quote.refused':          '#EF4444',
      'stock.low':              '#EF4444',
      'task.mention':           '#6366F1',
    };
    return colors[n.event_type] ?? this.moduleColor(n.module);
  }

  moduleIcon(module: string): string {
    const icons: Record<string, string> = {
      skedi: 'fa-light fa-check-square', kreosi: 'fa-light fa-file-invoice',
      logistiki: 'fa-light fa-truck', system: 'fa-light fa-cog',
      eezili: 'fa-light fa-globe',
    };
    return icons[module] ?? 'fa-light fa-bell';
  }

  moduleColor(module: string): string {
    const colors: Record<string, string> = {
      skedi: '#6366F1', kreosi: '#10B981',
      logistiki: '#F59E0B', system: '#6B7280',
    };
    return colors[module] ?? 'var(--primary-color)';
  }

  relativeTime(dateStr: string): string {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60)    return 'À l\'instant';
    if (diff < 3600)  return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return `Il y a ${Math.floor(diff / 86400)} j`;
  }
}
